import React from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Chip,
} from "@material-ui/core";
import { useLocation } from "react-router-dom";
import { useRoutePlans } from "../hooks/useRoutePlannerData";
import RouteLoadSheetContent from "../components/Routes/RouteLoadSheetContent";
import styles from "./RouteLoadSheetCollectionPage.module.css";

const parseStatuses = (searchParams) => {
  const collected = [];
  const multi = searchParams.getAll("status");
  if (multi.length) {
    multi.forEach((entry) => {
      entry
        .split(",")
        .map((val) => val.trim())
        .filter(Boolean)
        .forEach((val) => collected.push(val));
    });
  }
  return collected;
};

export default function RouteLoadSheetCollectionPage() {
  const location = useLocation();
  const searchParams = React.useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const from = searchParams.get("from") || "";
  const to = searchParams.get("to") || "";
  const statusFilters = React.useMemo(
    () => parseStatuses(searchParams),
    [searchParams]
  );
  const { data: plans = [], isLoading, isError, error } = useRoutePlans({
    from,
    to,
    status: statusFilters,
  });

  const [hasPrinted, setHasPrinted] = React.useState(false);
  React.useEffect(() => {
    if (
      plans.length &&
      searchParams.get("print") === "1" &&
      !hasPrinted
    ) {
      setHasPrinted(true);
      setTimeout(() => window.print(), 200);
    }
  }, [plans.length, searchParams, hasPrinted]);

  if (isLoading) {
    return (
      <Box p={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box p={4}>
        <Typography variant="h6" gutterBottom>
          Unable to load routes.
        </Typography>
        <Typography color="textSecondary">
          {error?.response?.data?.message || error?.message || "Unknown error."}
        </Typography>
      </Box>
    );
  }

  const dateFormatter = new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const rangeLabel = (() => {
    if (!from && !to) return "All dates";
    if (from && to && from === to) {
      return dateFormatter.format(new Date(from));
    }
    const start = from ? dateFormatter.format(new Date(from)) : "…";
    const end = to ? dateFormatter.format(new Date(to)) : "…";
    return `${start} → ${end}`;
  })();

  return (
    <div className={`${styles.pageRoot} route-printable`}>
      <Box className={styles.collectionHeader + " print-hidden"}>
        <div>
          <Typography variant="h5">Route Load Sheets</Typography>
          <Typography color="textSecondary">{rangeLabel}</Typography>
          {statusFilters.length > 0 && (
            <Box mt={1} display="flex" flexWrap="wrap" style={{ gap: 8 }}>
              {statusFilters.map((status) => (
                <Chip size="small" key={status} label={`Status: ${status}`} />
              ))}
            </Box>
          )}
        </div>
        <Button variant="contained" color="primary" onClick={() => window.print()}>
          Print
        </Button>
      </Box>
      {plans.length === 0 ? (
        <Box p={4} textAlign="center">
          <Typography variant="h6">No routes in this range.</Typography>
        </Box>
      ) : (
        <div className={styles.sheetStack}>
          {plans.map((plan, idx) => (
            <div key={plan._id || idx} className={styles.sheetWrapper}>
              <RouteLoadSheetContent
                plan={plan}
                className={styles.sheetInstance}
                pageBreakAfter
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
